import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import {
  Camera, MessageCircle, Instagram, Facebook, ArrowLeft, Copy, Check,
  Sparkles, Sun, Tent, Heart, Users, MapPin, Star,
} from "lucide-react";
import { SOCIAL } from "../lib/social";
import { withUtm } from "../lib/utm";

/**
 * /share/share-your-trip — 4-scene 9:16 reel pushing campers to share their
 * trips into the Campfire community.
 *
 * Bright sunny palette over the Orcaber Farm cover photo (real
 * user-supplied image, not stock). Every scene closes with a clear CTA
 * deep-linked back into a relevant community thread, so a share-to-Stories
 * also acts as a trip-report-funnel.
 *
 * Scenes:
 *   01 — THE INVITE: Orcaber field + "Share your trip"
 *   02 — WHAT TO SHARE: 4 thread categories with tap-through CTAs
 *   03 — WHY POST IT: 3 reasons + "kindest UK camping community"
 *   04 — THE LINK: QR + every social + 1-tap link to /community/new
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const COVER_IMG = "/images/orcaber-cover.jpg";

const COMMUNITY_URL = withUtm(`${PUBLIC_URL}/community`, {
  source: "trip-share",
  medium: "reel",
  campaign: "share-your-trip-2026",
});
const APP_URL = withUtm(PUBLIC_URL, {
  source: "trip-share",
  medium: "reel",
  campaign: "share-your-trip-app-install",
});

// Tap-targets per thread category — each deep-links into Community filtered
// by the tag, so a viewer who taps "Pets" lands straight on dog-camping posts.
const THREAD_HOOKS = [
  { icon: Camera,       title: "Trip Reports",  body: "Where you went · what made it · what surprised you.", tag: "Trip Reports" },
  { icon: Heart,        title: "Pets",          body: "Dog-friendly fields, paw-print stories, gear tips.",  tag: "Pets" },
  { icon: Users,        title: "Families",      body: "Kit-list wins, kid-friendly spots, school-holiday gold.", tag: "Families" },
  { icon: Sparkles,     title: "Positivity",    body: "Random acts of camping kindness · gratitude posts.",  tag: "Positivity" },
];

const CAPTION_LONG = `Share your trip 🏕️✨

That brilliant pitch you found last weekend? The dog-friendly field with the kindest owners? The campsite that surprised you mid-storm?

→ Pop it into Campfire Chat on CampCompass UK.

We're a small, kind, ad-free UK camping community — no algorithms shouting at you, no negativity. Just real campers swapping real stories so the next person finds the right patch.

Pick a thread, tell your story, drop a photo — it takes a minute and helps everyone.

✓ Trip Reports — where you went, what made it
✓ Pets — dog-friendly fields & paw-print stories
✓ Families — kit-list wins & kid-gold spots
✓ Positivity — kindness on the road

📸 ${SOCIAL?.instagram?.handle || "@camp.compassuk2026"}
👍 ${SOCIAL?.facebook?.handle || "@campcompassuk2026"}

camp-compass.uk · 22,160 UK campsites · install in 1 tap

#CampCompass #UKCamping #CampfireChat #ShareYourTrip`;

const CAPTION_SHORT = `Tell us about your last trip 🏕️
The kindest UK camping community — pitch in.

camp-compass.uk · Campfire Chat`;

function Sticker({ children, color = "#ff4500", textColor = "#ffffff" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em]"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

function Scene({ children, bg, testid, label }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500]">{label}</div>
      <section
        className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden shadow-[0_4px_0_0_#0e0e0e]"
        style={{ aspectRatio: "9 / 16", maxWidth: 380, background: bg }}
        data-testid={testid}
      >
        {children}
      </section>
    </div>
  );
}

function CaptionBox({ label, text }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border hairline bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">{label}</div>
        <button
          onClick={() => {
            navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
          className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#ff4500] hover:text-[#0e0e0e]"
          data-testid={`caption-copy-${label.split(" ")[0].toLowerCase()}`}
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap text-xs text-[#333] leading-relaxed font-sans">{text}</pre>
    </div>
  );
}

export default function ShareYourTripReel() {
  const iHandle = SOCIAL?.instagram?.handle || "@camp.compassuk2026";
  const fHandle = SOCIAL?.facebook?.handle || "@campcompassuk2026";

  return (
    <div className="min-h-screen bg-[#fffbf2]" data-testid="share-your-trip-reel">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link
          to="/launch-pack"
          className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#666] hover:text-[#0e0e0e] mb-4"
          data-testid="back-to-launch-pack"
        >
          <ArrowLeft className="w-3 h-3" /> Back to print pack
        </Link>

        <div className="eyebrow text-[#ff4500] mb-2 inline-flex items-center gap-2">
          <MessageCircle className="w-3 h-3" /> Campfire Chat · trip-share reel
        </div>
        <h1 className="font-display text-4xl tracking-tight mb-2">Share your trip · 4-scene reel · 9:16</h1>
        <p className="text-[#555] mb-6 max-w-2xl">
          Push existing campers to post into Campfire Chat. Real Orcaber Farm photo,
          four thread CTAs, one-tap deep-link into <code className="mono text-xs">/community</code>.
        </p>

        {/* Scenes */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* SCENE 1 — THE INVITE */}
          <Scene label="01 · The invite" testid="trip-scene-1" bg="#fbbf24">
            <div className="absolute inset-0">
              <img
                src={COVER_IMG}
                alt="Orcaber Farm camping in the Yorkshire Dales"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = "none"; }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70" aria-hidden="true" />
            </div>
            <div className="relative p-5 pt-7">
              <Sticker color="#ffffff" textColor="#0e0e0e">
                <Camera className="w-3 h-3" /> Share your trip
              </Sticker>
            </div>
            <div className="relative p-5 pb-7">
              <div className="font-display text-[34px] leading-[0.95] tracking-tight mb-3" style={{ textShadow: "0 2px 0 rgba(0,0,0,0.4)" }}>
                Tell us<br />
                <span className="text-[#fbbf24]">about it.</span>
              </div>
              <div className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-[0.2em] mb-1">
                <MapPin className="w-3 h-3" /> Orcaber Farm · Yorkshire Dales
              </div>
              <div className="text-sm leading-snug opacity-90 max-w-[260px]">
                That brilliant pitch you found last weekend — pop it in Campfire Chat.
              </div>
            </div>
          </Scene>

          {/* SCENE 2 — WHAT TO SHARE · four thread CTAs */}
          <Scene
            label="02 · What to share"
            testid="trip-scene-2"
            bg="linear-gradient(135deg, #16a34a 0%, #84cc16 50%, #fbbf24 100%)"
          >
            <div className="relative p-5 pt-7">
              <Sticker color="#0e0e0e">
                <Sparkles className="w-3 h-3" /> Pick a thread
              </Sticker>
            </div>
            <div className="relative p-5 space-y-2.5">
              {THREAD_HOOKS.map(({ icon: Icon, title, body }, i) => (
                <div key={i} className="bg-white/95 text-[#0e0e0e] p-3 shadow-[0_2px_0_0_#0e0e0e]">
                  <div className="flex items-start gap-2.5">
                    <div className="p-1.5 bg-[#ff4500] text-white flex-shrink-0">
                      <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-[13px] leading-tight">{title}</div>
                      <div className="text-[11px] text-[#555] leading-snug mt-0.5">{body}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="relative p-5 pb-7">
              <div className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-[0.2em] text-[#0e0e0e] bg-white/90 px-2 py-1">
                <Tent className="w-3 h-3" /> Takes 60 seconds · no negativity
              </div>
            </div>
          </Scene>

          {/* SCENE 3 — WHY POST IT */}
          <Scene
            label="03 · Why post it"
            testid="trip-scene-3"
            bg="linear-gradient(180deg, #fef3c7 0%, #fbbf24 55%, #f59e0b 100%)"
          >
            <div className="relative p-5 pt-7">
              <Sticker color="#0e0e0e">
                <Heart className="w-3 h-3 fill-current" /> Why share
              </Sticker>
            </div>
            <div className="relative px-5 text-[#0e0e0e] flex-1 flex flex-col justify-center">
              <div className="font-display text-[26px] leading-[0.95] tracking-tight mb-3">
                The kindest UK<br />
                <span className="text-[#0e0e0e]">camping community.</span>
              </div>
              <div className="space-y-2">
                {[
                  { Icon: Users,    body: "Real campers helping the next person find the right patch." },
                  { Icon: Star,     body: "Kudos & badges for honest, helpful posts." },
                  { Icon: Sun,      body: "No algorithms shouting. No negativity. Just stories." },
                ].map(({ Icon, body }, i) => (
                  <div key={i} className="bg-white/95 px-3 py-2 flex items-start gap-2 shadow-[0_2px_0_0_#0e0e0e]">
                    <Icon className="w-4 h-4 text-[#16a34a] flex-shrink-0 mt-0.5" strokeWidth={2.4} />
                    <div className="text-[12px] leading-snug">{body}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative p-5 pb-7">
              <div className="inline-flex items-center gap-1.5 text-[10px] mono uppercase tracking-[0.22em] text-[#0e0e0e] bg-white/90 px-2 py-1">
                <Sparkles className="w-3 h-3 text-[#ff4500]" /> Built for campers, by campers
              </div>
            </div>
          </Scene>

          {/* SCENE 4 — THE LINK · QR + socials + community CTA */}
          <Scene
            label="04 · Tap to post"
            testid="trip-scene-4"
            bg="linear-gradient(180deg, #0e0e0e 0%, #1a1a1a 100%)"
          >
            <div className="relative p-5 pt-7">
              <Sticker color="#ff4500">
                <MessageCircle className="w-3 h-3" /> Post in 1 tap
              </Sticker>
            </div>
            <div className="relative p-5 flex flex-col items-center text-center">
              <div className="font-display text-[24px] leading-[0.95] tracking-tight mb-3">
                Pitch in. <span className="text-[#ff4500]">Tell your story.</span>
              </div>
              <div className="text-[12px] text-white/80 mb-4 leading-snug max-w-[240px]">
                Scan → opens Campfire Chat → pick a thread → done.
              </div>
              <div className="bg-white p-3 border-2 border-[#ff4500] mb-4">
                <QRCodeSVG value={COMMUNITY_URL} size={130} level="M" bgColor="#ffffff" fgColor="#0e0e0e" />
              </div>
              <div className="text-[10px] mono uppercase tracking-[0.2em] text-[#ff4500] mb-3">
                camp-compass.uk/community
              </div>
              <div className="w-full space-y-1.5 text-[11px] mono">
                <div className="flex items-center gap-1.5 justify-center text-white/90">
                  <Instagram className="w-3.5 h-3.5 text-[#ff4500]" />
                  <span>{iHandle}</span>
                </div>
                <div className="flex items-center gap-1.5 justify-center text-white/90">
                  <Facebook className="w-3.5 h-3.5 text-[#ff4500]" />
                  <span>{fHandle}</span>
                </div>
              </div>
            </div>
            <div className="relative p-5 pb-7 text-center">
              <div className="text-[10px] mono uppercase tracking-[0.2em] text-white/60">
                #CampCompass · #CampfireChat · #ShareYourTrip
              </div>
            </div>
          </Scene>
        </div>

        {/* Captions */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <CaptionBox label="Long caption · Instagram + Facebook" text={CAPTION_LONG} />
          <CaptionBox label="Short caption · Stories + TikTok" text={CAPTION_SHORT} />
        </div>

        {/* Direct community deep-links — one per thread, so the reel description
            on Instagram/Facebook can carry a link straight into Pets, Families etc. */}
        <div className="border hairline bg-white p-5 mb-6">
          <div className="eyebrow mb-3">Direct thread links · paste into your bio / post</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {THREAD_HOOKS.map(({ tag }) => {
              const url = withUtm(`${PUBLIC_URL}/community?tag=${encodeURIComponent(tag)}`, {
                source: "trip-share",
                medium: "reel-bio",
                campaign: `share-${tag.toLowerCase().replace(/\s+/g, "-")}`,
              });
              return (
                <a
                  key={tag}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-2 px-3 py-2 border hairline text-xs hover:bg-[#fff4ee] hover:border-[#ff4500] transition-colors mono"
                  data-testid={`thread-deep-link-${tag.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="truncate">{tag}</span>
                  <span className="text-[#ff4500] text-[10px] uppercase tracking-wider">Open →</span>
                </a>
              );
            })}
          </div>
        </div>

        <a
          href={APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-5 py-3 bg-[#0e0e0e] text-white hover:bg-[#ff4500] transition-colors text-xs mono uppercase tracking-wider"
          data-testid="preview-app"
        >
          <Tent className="w-3.5 h-3.5" /> Preview the app
        </a>
      </div>
    </div>
  );
}
